import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Statistic,
  Tag,
  Button,
  Table,
  Modal,
  Radio,
  InputNumber,
  Space,
  Typography,
  Empty,
  Result,
  Divider,
  Layout,
  Switch,
  Tabs,
  Alert,
} from "antd";
import {
  WhatsAppOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  HistoryOutlined,
  MessageOutlined,
  WarningOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import {
  WHATSAPP_CONFIG,
  WHATSAPP_LOGS,
  WHATSAPP_RECHARGE_HISTORY,
  WHATSAPP_RECHARGE_ORDER,
  WHATSAPP_RECHARGE_VERIFY,
  WHATSAPP_MESSAGE_SENDING,
  WHATSAPP_RECHARGE_RECEIPT,
} from "../../constants/api";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import {
  renderWhatsappMessage,
  safeParseJson,
  WHATSAPP_TEMPLATE_LABELS,
} from "./whatsappTemplates";
import { WHATSAPP_NO } from "../../constants/constent";

const { Title, Text } = Typography;

// Status in whatsapp_logs: "1" = Sent (success), anything else = Failed.
const isSentStatus = (status) => String(status) === "1";

const renderStatusTag = (status) =>
  isSentStatus(status) ? (
    <Tag color="green">Sent</Tag>
  ) : (
    <Tag color="red">Failed</Tag>
  );

// Format a datetime string as e.g. "28 Aug 2026, 11:00 AM".
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Format integer paisa as a rupee string, e.g. 30 -> "₹0.30", 10000 -> "₹100.00".
// Uses integer division to avoid floating point drift on the authoritative value.
const paisaToRupeeLabel = (paisa) => {
  const value = Number.isFinite(paisa) ? paisa : 0;
  const rupees = Math.floor(value / 100);
  const remainder = Math.abs(value % 100)
    .toString()
    .padStart(2, "0");
  return `₹${rupees}.${remainder}`;
};

function WhatsappPage() {
  const { Content } = Layout;
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [togglingMsg, setTogglingMsg] = useState(false);

  // Recharge modal state
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState(null);
  const [paying, setPaying] = useState(false);

  // Log detail (View) modal state
  const [viewLog, setViewLog] = useState(null);

  // Logs state (server-side pagination)
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Recharge history state (server-side pagination)
  const [recharges, setRecharges] = useState([]);
  const [rechargesLoading, setRechargesLoading] = useState(false);
  const [rechargePagination, setRechargePagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    getAPI(
      WHATSAPP_CONFIG,
      (data) => {
        if (data.status === true) {
          setConfig(data.data);
        } else {
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Unable to load configuration",
          );
        }
        setConfigLoading(false);
      },
      () => {
        setConfigLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to load WhatsApp configuration");
      },
    );
  }, []);

  const loadLogs = useCallback((page = 1, pageSize = 20) => {
    setLogsLoading(true);
    getAPI(
      WHATSAPP_LOGS,
      (data) => {
        if (data.status === true) {
          setLogs((data.data || []).map((row) => ({ key: row.id, ...row })));
          setPagination({
            current: data.meta?.current_page || page,
            pageSize: data.meta?.per_page || pageSize,
            total: data.meta?.total || 0,
          });
        } else {
          displayMessage(ERROR_MSG_TYPE, data.message || "Unable to load logs");
        }
        setLogsLoading(false);
      },
      () => {
        setLogsLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to load WhatsApp logs");
      },
      { page, per_page: pageSize },
    );
  }, []);

  const loadRecharges = useCallback((page = 1, pageSize = 20) => {
    setRechargesLoading(true);
    getAPI(
      WHATSAPP_RECHARGE_HISTORY,
      (data) => {
        if (data.status === true) {
          setRecharges(
            (data.data || []).map((row) => ({ key: row.id, ...row })),
          );
          setRechargePagination({
            current: data.meta?.current_page || page,
            pageSize: data.meta?.per_page || pageSize,
            total: data.meta?.total || 0,
          });
        } else {
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Unable to load recharge history",
          );
        }
        setRechargesLoading(false);
      },
      () => {
        setRechargesLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to load recharge history");
      },
      { page, per_page: pageSize },
    );
  }, []);

  useEffect(() => {
    loadConfig();
    loadLogs(1, 20);
    loadRecharges(1, 20);
  }, [loadConfig, loadLogs, loadRecharges]);

  const handleTableChange = (pager) => {
    loadLogs(pager.current, pager.pageSize);
  };

  const handleRechargeTableChange = (pager) => {
    loadRecharges(pager.current, pager.pageSize);
  };

  // Toggle whether WhatsApp messages are actually sent (enable_whatsapp_msg).
  const toggleMessageSending = (checked) => {
    setTogglingMsg(true);
    postAPI(
      WHATSAPP_MESSAGE_SENDING,
      { enable_whatsapp_msg: checked ? 1 : 0 },
      (data) => {
        setTogglingMsg(false);
        if (data.status === true) {
          displayMessage(SUCCESS_MESSAGE, data.message);
          setConfig((prev) =>
            prev ? { ...prev, message_sending_enabled: !!checked } : prev,
          );
        } else {
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Unable to update message sending",
          );
        }
      },
      () => {
        setTogglingMsg(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to update message sending");
      },
    );
  };

  const openRecharge = () => {
    const presets = config?.recharge_presets || [];
    setSelectedAmount(presets.length ? presets[0] : null);
    setCustomAmount(null);
    setRechargeOpen(true);
  };

  const effectiveAmount = () => {
    if (customAmount && Number(customAmount) > 0) return Number(customAmount);
    return selectedAmount ? Number(selectedAmount) : 0;
  };

  const validateAmount = (amount) => {
    const min = config?.recharge_min ?? 1;
    const max = config?.recharge_max ?? Number.MAX_SAFE_INTEGER;
    if (!amount || amount <= 0) return "Please enter a valid amount";
    if (amount < min) return `Minimum recharge is ₹${min}`;
    if (amount > max) return `Maximum recharge is ₹${max}`;
    return null;
  };

  // Verify the payment with the backend. Wallet is credited ONLY here (server-side),
  // never based on the Razorpay frontend callback alone.
  const verifyPayment = (razorpayResponse) => {
    const body = {
      razorpay_order_id: razorpayResponse.razorpay_order_id,
      razorpay_payment_id: razorpayResponse.razorpay_payment_id,
      razorpay_signature: razorpayResponse.razorpay_signature,
    };

    postAPI(
      WHATSAPP_RECHARGE_VERIFY,
      body,
      (data) => {
        setPaying(false);
        if (data.status === true) {
          displayMessage(
            SUCCESS_MESSAGE,
            data.message || "Recharge successful",
          );
          setRechargeOpen(false);
          // Refresh authoritative wallet/config, logs and recharge history.
          loadConfig();
          loadLogs(1, pagination.pageSize);
          loadRecharges(1, rechargePagination.pageSize);
        } else {
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Payment verification failed",
          );
        }
      },
      () => {
        setPaying(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to verify payment");
      },
    );
  };

  const startRecharge = () => {
    const amount = effectiveAmount();
    const validationError = validateAmount(amount);
    if (validationError) {
      displayMessage(ERROR_MSG_TYPE, validationError);
      return;
    }

    if (!window.Razorpay) {
      displayMessage(ERROR_MSG_TYPE, "Payment gateway is not available");
      return;
    }

    setPaying(true);

    postAPI(
      WHATSAPP_RECHARGE_ORDER,
      { amount },
      (data) => {
        if (data.status !== true) {
          setPaying(false);
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Unable to create recharge order",
          );
          return;
        }

        const order = data.data;
        const options = {
          key: order.key,
          amount: order.amount_paisa, // already in paisa from backend
          currency: order.currency || "INR",
          name: order.name || "WhatsApp Wallet",
          description: order.description || "WhatsApp Wallet Recharge",
          order_id: order.order_id,
          handler: function (response) {
            // Payment captured on Razorpay side; verify server-side before crediting.
            verifyPayment(response);
          },
          modal: {
            ondismiss: function () {
              // User cancelled/closed the checkout. Do NOT credit anything.
              setPaying(false);
              displayMessage(ERROR_MSG_TYPE, "Payment cancelled");
            },
          },
          theme: {
            color: "#25D366",
          },
        };

        try {
          const rzp = new window.Razorpay(options);
          rzp.on("payment.failed", function (resp) {
            setPaying(false);
            displayMessage(
              ERROR_MSG_TYPE,
              resp?.error?.description || "Payment failed. Please try again.",
            );
          });
          rzp.open();
        } catch (e) {
          setPaying(false);
          displayMessage(ERROR_MSG_TYPE, "Unable to open payment window");
        }
      },
      () => {
        setPaying(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to create recharge order");
      },
    );
  };

  const columns = [
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 160,
      render: (phone) => phone || <Text type="secondary">—</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => renderStatusTag(status),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setViewLog(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const rechargeColumns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (value) => value || <Text type="secondary">—</Text>,
    },
    {
      title: "Amount",
      dataIndex: "amount_rupees",
      key: "amount_rupees",
      width: 120,
      render: (value) => `₹${Number(value ?? 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => {
        const color =
          status === "Successful"
            ? "green"
            : status === "Pending"
              ? "orange"
              : "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Payment ID",
      dataIndex: "razorpay_payment_id",
      key: "razorpay_payment_id",
      ellipsis: true,
      render: (value) => value || <Text type="secondary">—</Text>,
    },
    {
      title: "Balance After",
      dataIndex: "balance_after_rupees",
      key: "balance_after_rupees",
      width: 130,
      render: (value) =>
        value !== null && value !== undefined ? (
          `₹${Number(value).toFixed(2)}`
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Receipt",
      key: "receipt",
      width: 120,
      render: (_, record) =>
        record.status === "Successful" ? (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => {
              const encodedId = btoa(String(record.id));
              window.open(`${WHATSAPP_RECHARGE_RECEIPT}${encodedId}`, "_blank");
            }}
          >
            Receipt
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        {config && config.enabled === false ? (
          <Card>
            <Result
              status="warning"
              icon={<WhatsAppOutlined style={{ color: "#25D366" }} />}
              title="WhatsApp module is disabled"
              subTitle="WhatsApp messaging is not enabled for your account yet. Please contact your Software Administrator to enable the WhatsApp module and access messaging and wallet recharge."
              extra={
                <Button
                  type="primary"
                  onClick={() => {
                    const phone = WHATSAPP_NO;

                    const message =
                      "Hi, I need help enabling the WhatsApp module for my account. Please assist me with enabling WhatsApp messaging and wallet recharge.";

                    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(
                      message,
                    )}`;

                    window.open(whatsappUrl, "_blank");
                  }}
                >
                  Connect on WhatsApp
                </Button>
              }
            />
          </Card>
        ) : (
          <Card loading={configLoading}>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 16 }}
            >
              <Col>
                <Title level={3} style={{ margin: 0 }}>
                  <WhatsAppOutlined
                    style={{ color: "#25D366", marginRight: 8 }}
                  />
                  WhatsApp Sent Message Config
                </Title>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      loadConfig();
                      loadLogs(pagination.current, pagination.pageSize);
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openRecharge}
                  >
                    Recharge
                  </Button>
                </Space>
              </Col>
            </Row>

            {config?.low_balance ? (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
                message="Low WhatsApp balance"
                description={
                  config.message_points <= 0
                    ? "Your WhatsApp wallet is empty. Recharge now to keep sending messages."
                    : `Only ${config.message_points} message${
                        config.message_points === 1 ? "" : "s"
                      } left (threshold: ${config.low_balance_threshold}). Recharge soon to avoid interruptions.`
                }
                action={
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openRecharge}
                  >
                    Recharge
                  </Button>
                }
              />
            ) : null}

            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card>
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    <div>
                      <Text type="secondary">Module Status</Text>
                      <div style={{ marginTop: 4 }}>
                        {config?.enabled ? (
                          <Tag
                            color="green"
                            style={{ fontSize: 14, padding: "4px 10px" }}
                          >
                            Enabled
                          </Tag>
                        ) : (
                          <Tag
                            color="red"
                            style={{ fontSize: 14, padding: "4px 10px" }}
                          >
                            Disabled
                          </Tag>
                        )}
                      </div>
                    </div>

                    <div>
                      <Text type="secondary">Message Sending</Text>
                      <div style={{ marginTop: 4 }}>
                        <Space>
                          <Switch
                            checked={!!config?.message_sending_enabled}
                            loading={togglingMsg}
                            onChange={toggleMessageSending}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                          />
                          <Text type="secondary">
                            {config?.message_sending_enabled
                              ? "Messages are being sent"
                              : "Message sending is paused"}
                          </Text>
                        </Space>
                      </div>
                    </div>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="WhatsApp Wallet"
                    prefix={<WalletOutlined />}
                    value={config ? config.wallet_balance_rupees : 0}
                    precision={2}
                    suffix="₹"
                  />
                  <Text type="secondary">
                    {paisaToRupeeLabel(config?.wallet_balance_paisa || 0)} (
                    {config?.wallet_balance_paisa || 0} paisa)
                  </Text>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Available Messages"
                    prefix={<ThunderboltOutlined />}
                    value={config ? config.message_points : 0}
                  />
                  <Text type="secondary">
                    Price: {config?.message_price_paisa} paisa / message (
                    {paisaToRupeeLabel(config?.message_price_paisa || 0)})
                  </Text>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Tabs
              defaultActiveKey="logs"
              items={[
                {
                  key: "logs",
                  label: (
                    <span>
                      <MessageOutlined /> WhatsApp Logs
                    </span>
                  ),
                  children: (
                    <>
                      <Row justify="end" style={{ marginBottom: 12 }}>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={() =>
                            loadLogs(pagination.current, pagination.pageSize)
                          }
                        >
                          Refresh
                        </Button>
                      </Row>
                      <Table
                        columns={columns}
                        dataSource={logs}
                        loading={logsLoading}
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total,
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "20", "50", "100"],
                          showTotal: (total) => `Total ${total} logs`,
                        }}
                        onChange={handleTableChange}
                        locale={{
                          emptyText: (
                            <Empty description="No WhatsApp logs yet" />
                          ),
                        }}
                        scroll={{ x: 800 }}
                        size="middle"
                      />
                    </>
                  ),
                },
                {
                  key: "recharges",
                  label: (
                    <span>
                      <HistoryOutlined /> Recharge History
                    </span>
                  ),
                  children: (
                    <>
                      <Row justify="end" style={{ marginBottom: 12 }}>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={() =>
                            loadRecharges(
                              rechargePagination.current,
                              rechargePagination.pageSize,
                            )
                          }
                        >
                          Refresh
                        </Button>
                      </Row>
                      <Table
                        columns={rechargeColumns}
                        dataSource={recharges}
                        loading={rechargesLoading}
                        pagination={{
                          current: rechargePagination.current,
                          pageSize: rechargePagination.pageSize,
                          total: rechargePagination.total,
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "20", "50", "100"],
                          showTotal: (total) => `Total ${total} recharges`,
                        }}
                        onChange={handleRechargeTableChange}
                        locale={{
                          emptyText: <Empty description="No recharges yet" />,
                        }}
                        scroll={{ x: 800 }}
                        size="middle"
                      />
                    </>
                  ),
                },
              ]}
            />

            <Modal
              title="Recharge WhatsApp Wallet"
              open={rechargeOpen}
              onCancel={() => !paying && setRechargeOpen(false)}
              onOk={startRecharge}
              okText={paying ? "Processing..." : "Proceed to Pay"}
              confirmLoading={paying}
              okButtonProps={{ disabled: paying }}
              cancelButtonProps={{ disabled: paying }}
              maskClosable={!paying}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Text type="secondary">
                  Select a recharge amount in Indian Rupees (₹). The amount will
                  be credited to your WhatsApp wallet after successful payment.
                </Text>

                <Radio.Group
                  value={selectedAmount}
                  onChange={(e) => {
                    setSelectedAmount(e.target.value);
                    setCustomAmount(null);
                  }}
                  buttonStyle="solid"
                >
                  <Space wrap>
                    {(config?.recharge_presets || []).map((amt) => (
                      <Radio.Button key={amt} value={amt}>
                        ₹{amt}
                      </Radio.Button>
                    ))}
                  </Space>
                </Radio.Group>

                <div>
                  <Text>Or enter a custom amount (₹):</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: 8 }}
                    min={config?.recharge_min ?? 1}
                    max={config?.recharge_max ?? undefined}
                    placeholder={`Min ₹${config?.recharge_min ?? 1}`}
                    value={customAmount}
                    onChange={(val) => {
                      setCustomAmount(val);
                      if (val) setSelectedAmount(null);
                    }}
                  />
                </div>

                <Card size="small" style={{ background: "#fafafa" }}>
                  <Row justify="space-between">
                    <Text>Recharge amount</Text>
                    <Text strong>₹{effectiveAmount()}</Text>
                  </Row>
                  {config?.message_price_paisa ? (
                    <Row justify="space-between">
                      <Text type="secondary">Approx. messages</Text>
                      <Text type="secondary">
                        {Math.floor(
                          (effectiveAmount() * 100) /
                            config.message_price_paisa,
                        )}
                      </Text>
                    </Row>
                  ) : null}
                </Card>
              </Space>
            </Modal>

            <Modal
              title="WhatsApp Message Details"
              open={!!viewLog}
              onCancel={() => setViewLog(null)}
              footer={<Button onClick={() => setViewLog(null)}>Close</Button>}
              width={640}
            >
              {viewLog
                ? (() => {
                    const rendered = renderWhatsappMessage(viewLog.message);
                    const responseObj = safeParseJson(viewLog.response);
                    const templateLabel =
                      (rendered.templateName &&
                        WHATSAPP_TEMPLATE_LABELS[rendered.templateName]) ||
                      rendered.templateName ||
                      "—";

                    return (
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: "100%" }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Text type="secondary">Phone</Text>
                            <div>
                              <Text strong>{viewLog.phone || "—"}</Text>
                            </div>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Status</Text>
                            <div>{renderStatusTag(viewLog.status)}</div>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col span={12}>
                            <Text type="secondary">Template</Text>
                            <div>
                              <Text strong>{templateLabel}</Text>
                            </div>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Created At</Text>
                            <div>
                              <Text strong>
                                {formatDateTime(viewLog.created_at)}
                              </Text>
                            </div>
                          </Col>
                        </Row>

                        <Divider style={{ margin: "8px 0" }} />

                        <div>
                          <Text type="secondary">Message</Text>
                          <Card
                            size="small"
                            style={{ background: "#f6ffed", marginTop: 4 }}
                          >
                            {rendered.text ? (
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  margin: 0,
                                  fontFamily: "inherit",
                                }}
                              >
                                {rendered.text}
                              </pre>
                            ) : (
                              <Text type="secondary">
                                No message content available
                              </Text>
                            )}
                          </Card>
                        </div>

                        <div>
                          <Text type="secondary">Response (raw)</Text>
                          <Card
                            size="small"
                            style={{ background: "#fafafa", marginTop: 4 }}
                          >
                            <pre
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                margin: 0,
                                maxHeight: 240,
                                overflow: "auto",
                              }}
                            >
                              {responseObj
                                ? JSON.stringify(responseObj, null, 2)
                                : viewLog.response || "No response recorded"}
                            </pre>
                          </Card>
                        </div>
                      </Space>
                    );
                  })()
                : null}
            </Modal>
          </Card>
        )}
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default WhatsappPage;
